<template>
  <Section
    :id="id"
    :header="t('referenceData.requestedAccessorials', 2)"
    class="accessorial-section"
    data-class="flex flex-gap-1 flex-column"
  >
    <div class="accessorial-section-accessorials flex flex-gap-1 flex-wrap">
      <AccessorialInlineEdit
        v-for="(
          accessorial, topLevelAccessorialIndex
        ) in topLevelAccessorialsInternal"
        :key="accessorial.id"
        :model-value="accessorial"
        :accessorial-codes="getAvailableCodes(accessorial)"
        :editable="editable"
        @remove="onRemoveTopLevelAccessorial(topLevelAccessorialIndex)"
        @update:model-value="
          onUpdateTopLevelAccessorial(topLevelAccessorialIndex, $event)
        "
      />
      <template
        v-for="{
          accessorials: stopAccessorialsList,
          sequenceNumber,
        } in stopLevelAccessorialsInternal"
        :key="`stop-seq-${sequenceNumber}`"
      >
        <AccessorialInlineEdit
          v-for="(accessorial, accessorialIndexInStop) in stopAccessorialsList"
          :key="`${sequenceNumber}-${accessorialIndexInStop}`"
          :model-value="accessorial"
          :stop-sequence-number="sequenceNumber"
          :accessorial-codes="getAvailableCodes(accessorial, sequenceNumber)"
          :editable="editable"
          @remove="
            onRemoveStopLevelAccessorial(accessorialIndexInStop, sequenceNumber)
          "
          @update:model-value="
            onUpdateStopLevelAccessorial(
              accessorialIndexInStop,
              sequenceNumber,
              $event,
            )
          "
        />
      </template>
    </div>

    <div
      v-if="isAddingTopLevelAccessorial"
      class="accessorial-section-new formgrid grid"
    >
      <InputWrapper
        v-slot="{ ariaProps, inputId }"
        :input-id="`${id}-new-code`"
        :label="t('referenceData.accessorials')"
        class="field col-6"
      >
        <RygenDropdown
          :id="inputId"
          v-model="newTopLevelAccessorial"
          v-bind="ariaProps"
          :options="filteredTopLevelAccessorialCodes"
          option-label="name"
          data-key="naturalId"
          filter
          :placeholder="t('referenceData.accessorials')"
        />
      </InputWrapper>
    </div>

    <div
      class="accessorial-section-buttons flex flex-gap-1 justify-content-end"
    >
      <RygenButton
        v-if="isAddingTopLevelAccessorial"
        mode="tertiary"
        :label="t('common.remove')"
        icon="pi pi-times"
        icon-pos="left"
        @click="onCancelAddingTopLevelAccessorial"
      />
      <RygenButton
        v-if="isAddingTopLevelAccessorial"
        mode="tertiary"
        :disabled="!newTopLevelAccessorial?.id"
        :label="t('common.save')"
        icon="pi pi-check"
        icon-pos="left"
        @click="onSubmitTopLevelAccessorial"
      />
      <AddButton
        v-else-if="editable"
        :label="t('referenceData.accessorials')"
        @click="onAddTopLevelAccessorial"
      />
    </div>
  </Section>
</template>

<script lang="ts" setup>
import isEqual from 'lodash/isEqual';
import { computed, nextTick, PropType, ref, Ref, toRefs, watch } from 'vue';
import { useI18n } from 'vue-i18n';

import { useDirtyState } from '@rygen/rygen-ui/composables';
import { AccessorialCode } from '@rygen/rygen-ui/model';
import { alphabeticComparator } from '@rygen/rygen-ui/util';

import { useAccessorialCodeStore } from '@/stores/AccessorialCode';

import AccessorialInlineEdit from '@/components/accessorial/AccessorialInlineEdit.vue';
import AddButton from '@/components/ui/AddButton.vue';
import RygenButton from '@rygen/rygen-ui/components/button/RygenButton.vue';
import InputWrapper from '@rygen/rygen-ui/components/form/InputWrapper.vue';
import RygenDropdown from '@rygen/rygen-ui/components/form/RygenDropdown.vue';
import Section from '@rygen/rygen-ui/components/ui/Section.vue';

type StopLevelAccessorials = {
  sequenceNumber: number;
  accessorials: AccessorialCode[];
};

const props = defineProps({
  id: {
    type: String,
    required: true,
  },
  topLevelAccessorials: {
    type: Array as PropType<AccessorialCode[]>,
    default: () => [],
  },
  stopLevelAccessorials: {
    type: Array as PropType<StopLevelAccessorials[]>,
    default: () => [],
  },
  editable: {
    type: Boolean,
    default: () => false,
  },
});
const { id, topLevelAccessorials, editable, stopLevelAccessorials } =
  toRefs(props);
const emit = defineEmits<{
  (event: 'change', payload: { isDirty: boolean; numDirty: number }): void;
  (event: 'update:top-level-accessorials', newVal: AccessorialCode[]): void;
  (
    event: 'update:stop-level-accessorials',
    newVal: StopLevelAccessorials[],
  ): void;
}>();

const { accessorialCodes, loadRequestableAccessorialCodes } =
  useAccessorialCodeStore();
const { dirtyState, itemAdded, itemDeleted, itemEdited, resetDirtyState } =
  useDirtyState(topLevelAccessorials.value);
const { t } = useI18n();

const topLevelAccessorialsInternal: Ref<AccessorialCode[]> = ref([]);
const newTopLevelAccessorial: Ref<AccessorialCode | null> = ref(null);
const isAddingTopLevelAccessorial: Ref<boolean> = ref(false);

const stopLevelAccessorialsInternal: Ref<StopLevelAccessorials[]> = ref([]);
const originalStopLevelAccessorials: Ref<StopLevelAccessorials[]> = ref([]);

const numDirtyStopLevelAccessorials = computed(
  () =>
    stopLevelAccessorialsInternal.value.filter((stopData) => {
      const original = originalStopLevelAccessorials.value.find(
        (x) => x.sequenceNumber === stopData.sequenceNumber,
      );
      return !isEqual(
        stopData.accessorials.map((acc) => acc.id),
        original?.accessorials.map((acc) => acc.id) ?? [],
      );
    }).length,
);

const combinedDirtyState = computed(() => ({
  isDirty: dirtyState.value.isDirty || numDirtyStopLevelAccessorials.value > 0,
  numDirty: dirtyState.value.numDirty + numDirtyStopLevelAccessorials.value,
}));

const filteredTopLevelAccessorialCodes: Ref<AccessorialCode[]> = computed(
  () =>
    accessorialCodes.value.data?.filter(
      (code) =>
        !topLevelAccessorialsInternal.value.some((item) => item.id === code.id),
    ) ?? [],
);

const filteredStopLevelAccessorialCodes: Ref<Map<number, AccessorialCode[]>> =
  computed(() =>
    stopLevelAccessorialsInternal.value.reduce((curr, stopData) => {
      const availableAccessorialsForStop =
        accessorialCodes.value.data?.filter(
          (code) => !stopData.accessorials.some((item) => item.id === code.id),
        ) ?? [];
      curr.set(stopData.sequenceNumber, availableAccessorialsForStop);
      return curr;
    }, new Map<number, AccessorialCode[]>()),
  );

const getAvailableCodes = (
  currentlySelectedAccessorialCode: AccessorialCode,
  stopSequenceNumber?: number,
): AccessorialCode[] => {
  const filteredAccessorialCodes =
    (stopSequenceNumber === undefined
      ? filteredTopLevelAccessorialCodes.value
      : filteredStopLevelAccessorialCodes.value.get(stopSequenceNumber)) ?? [];

  const sortByName = alphabeticComparator({ isCaseSensitive: false });
  return [currentlySelectedAccessorialCode, ...filteredAccessorialCodes].sort(
    (a, b) => sortByName(a.name ?? '', b.name ?? ''),
  );
};

const onAddTopLevelAccessorial = () => {
  newTopLevelAccessorial.value = null;
  isAddingTopLevelAccessorial.value = true;
};

const onRemoveTopLevelAccessorial = (topLevelAccessorialIndex: number) => {
  const [deletedAccessorial] = topLevelAccessorialsInternal.value.splice(
    topLevelAccessorialIndex,
    1,
  );
  itemDeleted(topLevelAccessorialIndex, !!deletedAccessorial.id);
};

const onSubmitTopLevelAccessorial = () => {
  if (newTopLevelAccessorial.value?.id) {
    topLevelAccessorialsInternal.value.push(newTopLevelAccessorial.value);
    newTopLevelAccessorial.value = null;
    isAddingTopLevelAccessorial.value = false;
    itemAdded();
  }
};

const onCancelAddingTopLevelAccessorial = () => {
  newTopLevelAccessorial.value = null;
  isAddingTopLevelAccessorial.value = false;
};

const onUpdateTopLevelAccessorial = (
  topLevelAccessorialIndex: number,
  accessorial: AccessorialCode,
) => {
  itemEdited(
    topLevelAccessorialIndex,
    topLevelAccessorialsInternal.value[topLevelAccessorialIndex].naturalId !==
      accessorial.naturalId,
  );
  topLevelAccessorialsInternal.value[topLevelAccessorialIndex] =
    new AccessorialCode(accessorial);
};

const onRemoveStopLevelAccessorial = (
  accessorialIndexInStop: number,
  stopSequenceNumber: number,
) => {
  const stopData = stopLevelAccessorialsInternal.value.find(
    (x) => x.sequenceNumber === stopSequenceNumber,
  );
  if (stopData) {
    stopData.accessorials.splice(accessorialIndexInStop, 1);
  }
};

const onUpdateStopLevelAccessorial = (
  accessorialIndexInStop: number,
  stopSequenceNumber: number,
  accessorial: AccessorialCode,
) => {
  const stopData = stopLevelAccessorialsInternal.value.find(
    (x) => x.sequenceNumber === stopSequenceNumber,
  );
  if (stopData) {
    stopData.accessorials[accessorialIndexInStop] = new AccessorialCode(
      accessorial,
    );
  }
};

const cloneStopLevelAccessorials = (
  input: StopLevelAccessorials[],
): StopLevelAccessorials[] =>
  input.map((x) => ({
    sequenceNumber: x.sequenceNumber,
    accessorials: x.accessorials.map((acc) => new AccessorialCode(acc)),
  }));

const reset = () => {
  nextTick(() => {
    resetDirtyState(topLevelAccessorialsInternal.value);
    originalStopLevelAccessorials.value = cloneStopLevelAccessorials(
      stopLevelAccessorialsInternal.value,
    );
  });
};

watch(
  topLevelAccessorials,
  (newVal: AccessorialCode[]) => {
    if (!isEqual(newVal, topLevelAccessorialsInternal.value)) {
      resetDirtyState(newVal);
    }
    topLevelAccessorialsInternal.value = [...newVal];
  },
  { deep: true, immediate: true },
);

watch(
  topLevelAccessorialsInternal,
  (newVal: AccessorialCode[]) => {
    if (!isEqual(newVal, topLevelAccessorials.value)) {
      emit('change', combinedDirtyState.value);
      emit('update:top-level-accessorials', newVal);
    }
  },
  { deep: true },
);

watch(
  stopLevelAccessorials,
  (newVal) => {
    if (!isEqual(newVal, stopLevelAccessorialsInternal.value)) {
      stopLevelAccessorialsInternal.value = cloneStopLevelAccessorials(
        stopLevelAccessorials.value,
      ).sort((a, b) => a.sequenceNumber - b.sequenceNumber);
      originalStopLevelAccessorials.value = cloneStopLevelAccessorials(
        stopLevelAccessorialsInternal.value,
      );
    }
  },
  { deep: true, immediate: true },
);

watch(
  stopLevelAccessorialsInternal,
  (newVal) => {
    if (!isEqual(newVal, stopLevelAccessorials.value)) {
      emit('change', combinedDirtyState.value);
      emit(
        'update:stop-level-accessorials',
        cloneStopLevelAccessorials(newVal),
      );
    }
  },
  { deep: true },
);

watch(
  editable,
  (newVal: boolean) => {
    if (newVal) {
      loadRequestableAccessorialCodes();
    }
  },
  { immediate: true },
);

defineExpose({ reset });
</script>
